/**
 * Wizard card for the zapier_link step (Slice 5).
 *
 * The lightest integration card: the admin pastes the operator's Zapier webhook
 * URL (from a "Webhooks by Zapier" trigger), which `save_link` stores and
 * enables. The operator builds the Zap itself (Webhooks trigger → Limo Anywhere
 * "Create Reservation" action); this card only wires the platform side. A
 * non-gating test ping reports whether the URL is reachable.
 *
 * The webhook URL is NOT a secret, so — unlike the LA password / GHL key — it is
 * read back into the field on refresh. Status auto-refreshes via the run page's
 * 5s poll.
 */
import { useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery } from '@tanstack/react-query'
import { CheckCircle2, Loader2, RefreshCw, Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/lib/supabase'
import type { OnboardingTask } from '@limo/database'
import { useInvokeOnboardingStep } from '@/hooks/useOnboarding'
import { StepCard } from './StepCard'

interface ZapierCardProps {
  task: OnboardingTask
  accountId: string
  runId: string
}

interface ZapierPingResult {
  attempted: boolean
  ok?: boolean
  detail?: string
}

interface ZapierResult {
  webhookUrl?: string
  savedAt?: string
  testPing?: ZapierPingResult
}

const formSchema = z.object({
  webhookUrl: z
    .string()
    .trim()
    .url('Enter a valid webhook URL')
    .refine((u) => u.startsWith('https://'), 'Webhook URL must use https://'),
})

type FormData = z.infer<typeof formSchema>

export function ZapierCard({ task, accountId, runId }: ZapierCardProps) {
  const { toast } = useToast()
  const invokeStep = useInvokeOnboardingStep(runId)

  const result = (task.result ?? {}) as ZapierResult
  const isDone = task.status === 'done'

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { webhookUrl: '' },
  })

  // Read back the saved webhook URL so the field survives a refresh. The URL is
  // not a secret, so (unlike the LA/GHL cards) it is repopulated into the field.
  const { data: savedUrl } = useQuery({
    queryKey: ['onboarding-zapier-url', accountId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('account_settings')
        .select('value')
        .eq('account_id', accountId)
        .eq('key', 'zapier_webhook_url')
        .maybeSingle()
      if (error) throw new Error(error.message)
      return data?.value ?? ''
    },
  })

  // Seed once when the saved value first loads (a guard avoids clobbering edits
  // on a background refetch).
  const seeded = useRef(false)
  useEffect(() => {
    if (savedUrl === undefined || seeded.current) return
    seeded.current = true
    form.reset({ webhookUrl: savedUrl })
  }, [savedUrl, form])

  const save = form.handleSubmit((values) => {
    invokeStep.mutate(
      {
        functionName: 'onboard-zapier',
        accountId,
        params: { action: 'save_link', webhookUrl: values.webhookUrl },
      },
      {
        onError: (error) =>
          toast({
            title: 'Zapier link failed',
            description: error.message,
            variant: 'destructive',
          }),
      }
    )
  })

  const ping = result.testPing
  const pingWarned = isDone && ping?.attempted && !ping.ok

  return (
    <StepCard
      task={task}
      manualFallback={
        <p>
          Build the Zap in Zapier: a <strong>Webhooks by Zapier</strong> trigger
          (Catch Hook) → a <strong>Limo Anywhere</strong> &ldquo;Create
          Reservation&rdquo; action. Copy the Catch Hook URL into the field
          above. The payload includes <code>vehicle_code</code>,{' '}
          <code>service_code</code>, and <code>account_slug</code> for mapping.
          This step is safe to re-run.
        </p>
      }
    >
      <form onSubmit={save} className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor="zapier-url">
            Zapier webhook URL
            {isDone && (
              <span className="ml-2 inline-flex items-center gap-1 text-xs text-green-600">
                <CheckCircle2 className="h-3 w-3" /> saved
              </span>
            )}
          </Label>
          <Input
            id="zapier-url"
            placeholder="https://hooks.zapier.com/hooks/catch/..."
            {...form.register('webhookUrl')}
          />
          {form.formState.errors.webhookUrl && (
            <p className="text-xs text-red-600">
              {form.formState.errors.webhookUrl.message}
            </p>
          )}
        </div>

        {pingWarned && (
          <p className="text-xs text-amber-700">
            Saved, but the test ping didn&rsquo;t reach the webhook
            {ping?.detail ? ` (${ping.detail})` : ''}. That&rsquo;s expected if
            the Zap isn&rsquo;t published yet — re-save once it&rsquo;s live to
            re-check.
          </p>
        )}

        <Button type="submit" size="sm" disabled={invokeStep.isPending}>
          {invokeStep.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : isDone ? (
            <RefreshCw className="mr-2 h-4 w-4" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          {isDone ? 'Update webhook' : 'Save webhook'}
        </Button>
      </form>
    </StepCard>
  )
}
